import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { layoutForPath } from '../utils/layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Trash2, Pencil, ShieldCheck, Tag, Paperclip, Upload, Loader2, Download, FileText,
  AlertTriangle, Clock, CheckCircle2, Users,
} from 'lucide-react';
import DateField from '../components/DateField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COMPANY = '__company__';
const NONE = '__none__';

const emptyForm = {
  title: '', doc_type: '', holder_type: 'user', holder_user_id: '',
  issuer: '', doc_number: '', issue_date: '', expiry_date: '', notes: '',
};

const STATUS_META = {
  expired: { label: 'Expired', cls: 'bg-red-100 text-red-800 border-red-200' },
  expiring_soon: { label: 'Expiring soon', cls: 'bg-amber-100 text-amber-900 border-amber-200' },
  valid: { label: 'Valid', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  no_expiry: { label: 'No expiry', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

function StatusChip({ doc }) {
  const meta = STATUS_META[doc.status] || STATUS_META.no_expiry;
  const days = doc.days_remaining;
  let suffix = '';
  if (doc.status === 'expired') suffix = ` · ${Math.abs(days)}d ago`;
  else if (doc.status === 'expiring_soon') suffix = ` · ${days}d`;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${meta.cls}`}
      data-testid={`safety-doc-status-${doc.id}`}
    >
      {meta.label}{suffix}
    </span>
  );
}

function Attachments({ docId, files, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const inputRef = useRef(null);

  const upload = async (fileList) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append('file', f);
        await axios.post(`${API}/safety-docs/${docId}/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success(list.length === 1 ? 'File uploaded' : `${list.length} files uploaded`);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const open = async (att) => {
    setOpeningId(att.id);
    try {
      const url = att.url.startsWith('http') ? att.url : `${process.env.REACT_APP_BACKEND_URL}${att.url}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: att.content_type || 'application/octet-stream' }));
      const win = window.open(blobUrl, '_blank');
      if (!win) {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = att.filename;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch {
      toast.error('Failed to open file');
    } finally {
      setOpeningId(null);
    }
  };

  const remove = async (att) => {
    try {
      await axios.delete(`${API}/safety-docs/${docId}/files/${att.id}`);
      toast.success('Attachment removed');
      onChanged();
    } catch {
      toast.error('Failed to remove');
    }
  };

  return (
    <div className="rounded-md border border-slate-200 p-3" data-testid="safety-doc-attachments">
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
          <Paperclip size={14} /> Scanned copies ({files.length})
        </span>
        <input ref={inputRef} type="file" multiple className="hidden"
          onChange={(e) => upload(e.target.files)} data-testid="safety-doc-file-input" />
        <Button type="button" size="sm" variant="outline" disabled={uploading}
          onClick={() => inputRef.current?.click()} data-testid="safety-doc-upload-btn">
          {uploading ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Upload size={13} className="mr-1.5" />}
          {uploading ? 'Uploading…' : 'Upload'}
        </Button>
      </div>
      {files.length === 0 ? (
        <p className="text-xs text-slate-500">No files yet — attach the certificate scan or photo of the ticket.</p>
      ) : (
        <div className="space-y-1.5">
          {files.map((a) => (
            <div key={a.id} className="flex min-w-0 items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm"
              data-testid={`safety-doc-file-${a.id}`}>
              <FileText size={14} className="shrink-0 text-slate-500" />
              <button type="button" onClick={() => open(a)} className="min-w-0 flex-1 truncate text-left hover:underline">
                {a.filename}
              </button>
              <span className="shrink-0 text-[11px] text-slate-400">{fmtSize(a.size)}</span>
              <button type="button" onClick={() => open(a)} disabled={openingId === a.id}
                className="rounded p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-900" title="Open">
                {openingId === a.id ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
              </button>
              <button type="button" onClick={() => remove(a)}
                className="rounded p-1 text-red-500 hover:bg-red-50" title="Remove"
                data-testid={`safety-doc-file-remove-${a.id}`}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SafetyDocs() {
  const [docs, setDocs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [types, setTypes] = useState([]);
  const [holders, setHolders] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [holderFilter, setHolderFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [typeDialog, setTypeDialog] = useState(false);
  const [newTypeLabel, setNewTypeLabel] = useState('');

  const Layout = layoutForPath(useLocation().pathname);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.doc_status = statusFilter;
      if (holderFilter) params.holder_user_id = holderFilter === COMPANY ? 'company' : holderFilter;
      if (typeFilter) params.doc_type = typeFilter;
      const [docRes, sumRes] = await Promise.all([
        axios.get(`${API}/safety-docs`, { params }),
        axios.get(`${API}/safety-docs/summary`),
      ]);
      setDocs(docRes.data || []);
      setSummary(sumRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load safety documents');
    }
  }, [statusFilter, holderFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/safety-doc-types`).then((r) => setTypes(r.data.types || [])).catch(() => {});
    axios.get(`${API}/safety-docs/holders`).then((r) => setHolders(r.data || [])).catch(() => {});
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialog(true);
  };

  const openEdit = (d) => {
    setEditing(d);
    setForm({
      title: d.title || '', doc_type: d.doc_type || '',
      holder_type: d.holder_type || 'user', holder_user_id: d.holder_user_id || '',
      issuer: d.issuer || '', doc_number: d.doc_number || '',
      issue_date: d.issue_date || '', expiry_date: d.expiry_date || '', notes: d.notes || '',
    });
    setDialog(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Document name required'); return; }
    if (form.holder_type === 'user' && !form.holder_user_id) {
      toast.error('Pick who holds this document, or switch it to company-wide');
      return;
    }
    const payload = {
      title: form.title.trim(),
      doc_type: form.doc_type || null,
      holder_type: form.holder_type,
      holder_user_id: form.holder_type === 'user' ? form.holder_user_id : null,
      issuer: form.issuer || null,
      doc_number: form.doc_number || null,
      issue_date: form.issue_date || null,
      expiry_date: form.expiry_date || null,
      notes: form.notes || null,
    };
    try {
      if (editing) {
        const res = await axios.patch(`${API}/safety-docs/${editing.id}`, payload);
        setEditing(res.data);
        toast.success('Document updated');
      } else {
        const res = await axios.post(`${API}/safety-docs`, payload);
        setEditing(res.data);
        toast.success('Document added — attach a scan below if you have one');
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"? Attached files will be removed too.`)) return;
    try {
      await axios.delete(`${API}/safety-docs/${d.id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const refreshEditing = async () => {
    await load();
    try {
      const res = await axios.get(`${API}/safety-docs`, { params: {} });
      const fresh = (res.data || []).find((d) => d.id === editing?.id);
      if (fresh) setEditing(fresh);
    } catch { /* keep current */ }
  };

  const addType = async (e) => {
    e.preventDefault();
    const label = newTypeLabel.trim();
    if (!label) return;
    try {
      await axios.post(`${API}/safety-doc-types`, { label });
      const r = await axios.get(`${API}/safety-doc-types`);
      setTypes(r.data.types || []);
      setNewTypeLabel('');
      toast.success(`Added "${label}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add type');
    }
  };

  const removeType = async (t) => {
    if (!window.confirm(`Delete type "${t.label}"? Documents using it will be left untyped.`)) return;
    try {
      await axios.delete(`${API}/safety-doc-types/${t.value}`);
      const r = await axios.get(`${API}/safety-doc-types`);
      setTypes(r.data.types || []);
      load();
      toast.success('Type deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const typeLabel = (v) => types.find((t) => t.value === v)?.label || v;

  const q = search.trim().toLowerCase();
  const filtered = docs.filter((d) => !q
    || (d.title || '').toLowerCase().includes(q)
    || (d.holder_name || '').toLowerCase().includes(q)
    || (d.issuer || '').toLowerCase().includes(q)
    || (d.doc_number || '').toLowerCase().includes(q));

  const cards = [
    { key: '', label: 'All docs', value: summary?.total ?? 0, icon: ShieldCheck, cls: 'text-slate-700' },
    { key: 'expired', label: 'Expired', value: summary?.expired ?? 0, icon: AlertTriangle, cls: 'text-red-600' },
    { key: 'expiring_soon', label: 'Expiring in 60 days', value: summary?.expiring_soon ?? 0, icon: Clock, cls: 'text-amber-600' },
    { key: 'valid', label: 'Current', value: summary?.valid ?? 0, icon: CheckCircle2, cls: 'text-emerald-600' },
  ];

  const customTypes = types.filter((t) => !t.system);

  return (
    <Layout title="Safety Docs">
      <div className="space-y-6" data-testid="safety-docs-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="mb-1 flex items-center gap-2 text-2xl font-bold text-slate-900">
              <ShieldCheck size={22} /> Safety Docs Hub
            </h3>
            <p className="text-sm text-slate-600">
              Certificates, tickets, licences and training records with expiry dates. Reminders go out at 60, 30 and 7 days, and again the day they lapse.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setTypeDialog(true)} data-testid="manage-doc-types-button">
              <Tag size={16} className="mr-1" /> Manage Types
            </Button>
            <Button onClick={openAdd} className="bg-slate-900 hover:bg-slate-800" data-testid="add-safety-doc-button">
              <Plus size={18} className="mr-1" /> Add Document
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {cards.map((c) => {
            const Icon = c.icon;
            const active = statusFilter === c.key;
            return (
              <button
                key={c.key || 'all'}
                type="button"
                onClick={() => setStatusFilter(c.key)}
                className={`rounded-md border bg-white p-4 text-left shadow-sm transition-colors ${
                  active ? 'border-slate-900 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-400'
                }`}
                data-testid={`safety-doc-card-${c.key || 'all'}`}
              >
                <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wide ${c.cls}`}>
                  <Icon size={14} /> <span>{c.label}</span>
                </div>
                <div className="mt-2 text-3xl font-black text-slate-900">{c.value}</div>
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Input placeholder="Search by name, holder, issuer or number" value={search}
            onChange={(e) => setSearch(e.target.value)} className="max-w-sm" data-testid="safety-doc-search" />
          <Select value={holderFilter || NONE} onValueChange={(v) => setHolderFilter(v === NONE ? '' : v)}>
            <SelectTrigger className="w-56" data-testid="safety-doc-holder-filter"><SelectValue placeholder="All holders" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>All holders</SelectItem>
              <SelectItem value={COMPANY}>Company-wide</SelectItem>
              {holders.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={typeFilter || NONE} onValueChange={(v) => setTypeFilter(v === NONE ? '' : v)}>
            <SelectTrigger className="w-44" data-testid="safety-doc-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>All types</SelectItem>
              {types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-500">{filtered.length} shown</span>
        </div>

        <div className="rounded-md border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="safety-docs-table">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  {['Document', 'Holder', 'Type', 'Number', 'Issued', 'Expires', 'Status', 'Files', ''].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filtered.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50" data-testid={`safety-doc-row-${d.id}`}>
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{d.title}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {d.holder_type === 'company'
                        ? <span className="inline-flex items-center gap-1 text-slate-600"><Users size={13} /> Company-wide</span>
                        : d.holder_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">{d.doc_type ? typeLabel(d.doc_type) : <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{d.doc_number || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{d.issue_date || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{d.expiry_date || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3"><StatusChip doc={d} /></td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {(d.attachments || []).length > 0
                        ? <span className="inline-flex items-center gap-1"><Paperclip size={13} /> {(d.attachments || []).length}</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEdit(d)} data-testid={`edit-safety-doc-${d.id}`}>
                          <Pencil size={14} />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => remove(d)}
                          className="text-red-600 hover:bg-red-50" data-testid={`delete-safety-doc-${d.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="p-12 text-center text-slate-500" data-testid="safety-docs-empty">
              {docs.length === 0
                ? 'No safety documents yet. Add your first certificate or ticket to start tracking expiry dates.'
                : 'No documents match your filters.'}
            </div>
          )}
        </div>

        {/* Add / Edit dialog */}
        <Dialog open={dialog} onOpenChange={setDialog}>
          <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Document' : 'Add Document'}</DialogTitle>
              <DialogDescription>
                Everyone with Safety Docs access sees these records. Expiry dates drive the renewal reminders.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={save} className="space-y-3" data-testid="safety-doc-form">
              <div className="space-y-1">
                <Label className="text-xs">Document Name *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Fall Protection, Class 3 Driver's Licence" required data-testid="safety-doc-title-input" />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs">Type</Label>
                  <Select value={form.doc_type || NONE} onValueChange={(v) => setForm({ ...form, doc_type: v === NONE ? '' : v })}>
                    <SelectTrigger data-testid="safety-doc-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— none —</SelectItem>
                      {types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Holder</Label>
                  <Select
                    value={form.holder_type === 'company' ? COMPANY : (form.holder_user_id || NONE)}
                    onValueChange={(v) => setForm({
                      ...form,
                      holder_type: v === COMPANY ? 'company' : 'user',
                      holder_user_id: v === COMPANY || v === NONE ? '' : v,
                    })}
                  >
                    <SelectTrigger data-testid="safety-doc-holder-select"><SelectValue placeholder="Select holder" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— select —</SelectItem>
                      <SelectItem value={COMPANY}>Company-wide</SelectItem>
                      {holders.map((h) => (
                        <SelectItem key={h.id} value={h.id}>{h.name} · {(h.role || '').replace(/_/g, ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs">Issued By</Label>
                  <Input value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })}
                    placeholder="e.g. Red Cross, WorkSafe" data-testid="safety-doc-issuer-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Document / Ticket Number</Label>
                  <Input value={form.doc_number} onChange={(e) => setForm({ ...form, doc_number: e.target.value })}
                    data-testid="safety-doc-number-input" />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs">Issue Date</Label>
                  <DateField value={form.issue_date} onChange={(v) => setForm({ ...form, issue_date: v })}
                    data-testid="safety-doc-issue-date" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Expiry Date</Label>
                  <DateField value={form.expiry_date} onChange={(v) => setForm({ ...form, expiry_date: v })}
                    data-testid="safety-doc-expiry-date" />
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] text-slate-500">Blank = never expires.</p>
                    {form.expiry_date && (
                      <button type="button" onClick={() => setForm({ ...form, expiry_date: '' })}
                        className="text-[11px] font-medium text-slate-500 underline hover:text-slate-800"
                        data-testid="safety-doc-clear-expiry">Clear</button>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2} data-testid="safety-doc-notes-input" />
              </div>

              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-safety-doc">
                {editing ? 'Save Changes' : 'Add Document'}
              </Button>
            </form>

            {editing && (
              <div className="pt-2">
                <Attachments docId={editing.id} files={editing.attachments || []} onChanged={refreshEditing} />
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Manage types dialog */}
        <Dialog open={typeDialog} onOpenChange={setTypeDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Document Types</DialogTitle>
              <DialogDescription>Certificate, Ticket, License and Training are always available. Add your own below.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4" data-testid="manage-doc-types-body">
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Default</div>
                <div className="flex flex-wrap gap-2">
                  {types.filter((t) => t.system).map((t) => (
                    <span key={t.value} className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                      {t.label}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Custom</div>
                {customTypes.length === 0 ? (
                  <p className="text-sm italic text-slate-500">No custom types yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {customTypes.map((t) => (
                      <span key={t.value} className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-800"
                        data-testid={`custom-doc-type-${t.value}`}>
                        {t.label}
                        <button type="button" onClick={() => removeType(t)} className="text-blue-700 hover:text-red-600"
                          aria-label={`Delete ${t.label}`} data-testid={`delete-doc-type-${t.value}`}>
                          <Trash2 size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <form onSubmit={addType} className="flex gap-2 border-t border-slate-200 pt-3" data-testid="add-doc-type-form">
                <Input placeholder="e.g. Confined Space" value={newTypeLabel} maxLength={40}
                  onChange={(e) => setNewTypeLabel(e.target.value)} data-testid="new-doc-type-input" />
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="submit-doc-type">
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </form>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
